import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Performance summary states
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [averageCTR, setAverageCTR] = useState(0);
  const [bestPerformer, setBestPerformer] = useState(null);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await featuredPropertiesService?.getFeaturedPropertyAnalytics();
      
      if (fetchError) {
        setError(`Erreur lors du chargement des analytics: ${fetchError}`);
        return;
      }

      setAnalyticsData(data || []);
      
      // Calculate summary metrics
      if (data?.length > 0) {
        const clicks = data?.reduce((sum, item) => sum + (item?.click_count || 0), 0);
        const inquiries = data?.reduce((sum, item) => sum + (item?.inquiry_count || 0), 0);
        const ctr = clicks > 0 ? ((inquiries / clicks) * 100) : 0;
        const best = data?.reduce((prev, current) => 
          (current?.click_count > prev?.click_count) ? current : prev
        );

        setTotalClicks(clicks);
        setTotalInquiries(inquiries);
        setAverageCTR(ctr);
        setBestPerformer(best);
      }
    } catch (err) {
      setError(`Erreur inattendue: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const formatPrice = (price) => {
    if (!price) return 'Prix non défini';
    const numPrice = parseInt(price);
    const formatted = (numPrice / 1000000)?.toFixed(0);
    return `${formatted}M XOF`;
  };

  const calculateCTR = (clicks, inquiries) => {
    if (clicks === 0) return '0%';
    return `${((inquiries / clicks) * 100)?.toFixed(1)}%`;
  };

  const getPerformanceColor = (clicks) => {
    if (clicks >= 100) return 'text-green-600';
    if (clicks >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (clicks) => {
    if (clicks >= 100) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (clicks >= 50) return { label: 'Bon', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'À améliorer', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-brand-earth mx-auto mb-4" />
          <p className="text-lg text-text-secondary">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Performance Overview */}
      <div className="bg-white rounded-lg shadow-warm p-6">
        <h2 className="text-xl font-heading font-bold text-text-primary mb-6">
          Vue d'Ensemble des Performances
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="bg-blue-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <Icon name="MousePointer" size={24} className="text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-text-primary">{totalClicks?.toLocaleString()}</h3>
            <p className="text-text-secondary">Total des Clics</p>
          </div>

          <div className="text-center">
            <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <Icon name="MessageSquare" size={24} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-text-primary">{totalInquiries?.toLocaleString()}</h3>
            <p className="text-text-secondary">Total des Demandes</p>
          </div>

          <div className="text-center">
            <div className="bg-purple-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <Icon name="TrendingUp" size={24} className="text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-text-primary">{averageCTR?.toFixed(1)}%</h3>
            <p className="text-text-secondary">Taux de Conversion Moyen</p>
          </div>

          <div className="text-center">
            <div className="bg-yellow-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <Icon name="Star" size={24} className="text-yellow-600" />
            </div>
            <h3 className="text-lg font-bold text-text-primary line-clamp-1">
              {bestPerformer?.property?.title || 'N/A'}
            </h3>
            <p className="text-text-secondary">Meilleur Performant</p>
          </div>
        </div>
      </div>

      {/* Detailed Analytics Table */}
      <div className="bg-white rounded-lg shadow-warm overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-heading font-bold text-text-primary">
            Performance par Propriété
          </h2>
        </div>

        {analyticsData?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="BarChart3" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">Aucune donnée analytique disponible</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface">
                <tr>
                  <th className="text-left p-4 font-medium text-text-primary">Propriété</th>
                  <th className="text-left p-4 font-medium text-text-primary">Ordre</th>
                  <th className="text-left p-4 font-medium text-text-primary">Statut</th>
                  <th className="text-right p-4 font-medium text-text-primary">Clics</th>
                  <th className="text-right p-4 font-medium text-text-primary">Demandes</th>
                  <th className="text-right p-4 font-medium text-text-primary">CTR</th>
                  <th className="text-center p-4 font-medium text-text-primary">Performance</th>
                  <th className="text-right p-4 font-medium text-text-primary">Prix</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData?.map((item, index) => {
                  const performanceBadge = getPerformanceBadge(item?.click_count || 0);
                  
                  return (
                    <tr key={item?.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-surface/50'}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand-earth/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon name="Home" size={16} className="text-brand-earth" />
                          </div>
                          <div>
                            <h3 className="font-medium text-text-primary line-clamp-1">
                              {item?.property?.title || 'Titre non disponible'}
                            </h3>
                            <p className="text-sm text-text-secondary">
                              Créé le {new Date(item?.created_at)?.toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                          <Icon name="Hash" size={12} />
                          {item?.display_order}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item?.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span className={`font-bold ${getPerformanceColor(item?.click_count || 0)}`}>
                          {(item?.click_count || 0)?.toLocaleString()}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-medium text-text-primary">
                          {(item?.inquiry_count || 0)?.toLocaleString()}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-medium text-text-primary">
                          {calculateCTR(item?.click_count || 0, item?.inquiry_count || 0)}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${performanceBadge?.color}`}>
                          {performanceBadge?.label}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-bold text-brand-earth">
                          {formatPrice(item?.property?.price)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Performance Tips */}
      <div className="bg-white rounded-lg shadow-warm p-6">
        <h2 className="text-xl font-heading font-bold text-text-primary mb-4">
          Conseils d'Optimisation
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Icon name="Target" size={20} className="text-blue-600 mr-2" />
              <h3 className="font-semibold text-blue-900">Position Stratégique</h3>
            </div>
            <p className="text-sm text-blue-800">
              Les propriétés en première position reçoivent généralement 40% de clics en plus.
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Icon name="Image" size={20} className="text-green-600 mr-2" />
              <h3 className="font-semibold text-green-900">Images de Qualité</h3>
            </div>
            <p className="text-sm text-green-800">
              Utilisez des images hero personnalisées pour augmenter l'engagement de 60%.
            </p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Icon name="PenTool" size={20} className="text-purple-600 mr-2" />
              <h3 className="font-semibold text-purple-900">Titres Accrocheurs</h3>
            </div>
            <p className="text-sm text-purple-800">
              Des titres personnalisés peuvent améliorer le taux de clic de 25%.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;